import React, { useState } from 'react';
import clsx from 'clsx';
import styles from '../WarrantyRequestModal.module.scss';
import { Input } from '../../../../components/uikit/inputs/Input.tsx';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { Autocomplete, TextField } from '@mui/material';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import PrismaSmart from 'assets/icons/prismaSmart.svg?react';
import PrismaLab from 'assets/icons/prismaLab.svg?react';
import PrismaVent from 'assets/icons/prismaVent.svg?react';
import DefaultIcon from 'assets/icons/device-placeholder.svg?react';
import Luisa from 'assets/icons/luisa.svg?react';
import Caro from 'assets/icons/caro.svg?react';
import { AdvancedFormReturnType } from '../../../../helpers/form/useAdvancedForm.ts';
import { Form, Option } from '../WarrantyRequestModal.tsx';
import { FoundDeviceDto } from '../../../../services/api/api-client.types.ts';
import ArrowDown from 'assets/icons/arrow-down-selector.svg?react';
import { SelectOption } from './SelectOption.tsx';
import CalendarIcon from 'assets/icons/calendar.svg?react';
import { dateFormat } from '../../../../helpers/date-helpers.ts';

type OwnProps = {
  form: AdvancedFormReturnType<Form>;
  device: FoundDeviceDto | null | undefined;
  dateOfResale: Date | null;
  serialNumber: string | null;
  deviceTypes?: Option[];
};

const prismaSmartRegex = /prisma\s*(smart|soft)/;
const prismaLabRegex = /prisma\s*(lab|cr|20|25|30)/;
const prismaVentRegex = /prisma\s*(vent\s*30|vent\s*40|vent\s*50)/;
const luisaRegex = /luisa/;
const caroRegex = /caro|grundgerät h5 mit akku|grundgeraet h5 mit akku/;

export const defineDeviceImage = (
  deviceName: string,
  customClassName?: string,
  isDefaultIconNeeded?: boolean,
) => {
  const loweredDeviceName = deviceName.toLowerCase();

  if (prismaSmartRegex.test(loweredDeviceName)) {
    return (
      <div className={clsx(styles.imageContainer, customClassName)}>
        <PrismaSmart />
      </div>
    );
  }

  if (prismaLabRegex.test(loweredDeviceName)) {
    return (
      <div className={clsx(styles.imageContainer, customClassName)}>
        <PrismaLab />
      </div>
    );
  }

  if (prismaVentRegex.test(loweredDeviceName)) {
    return (
      <div className={clsx(styles.imageContainer, customClassName)}>
        <PrismaVent />
      </div>
    );
  }

  if (luisaRegex.test(loweredDeviceName)) {
    return (
      <div className={clsx(styles.imageContainer, customClassName)}>
        <Luisa />
      </div>
    );
  }

  if (caroRegex.test(loweredDeviceName)) {
    return (
      <div className={clsx(styles.imageContainer, customClassName)}>
        <Caro />
      </div>
    );
  }

  if (isDefaultIconNeeded) {
    return (
      <div className={clsx(styles.imageContainer, customClassName)}>
        <DefaultIcon />
      </div>
    );
  }
};

export const SerialNumberInfo = (props: OwnProps) => {
  const { device, form, dateOfResale, serialNumber, deviceTypes = [] } = props;

  const [inputValue, setInputValue] = useState('');

  const i18n = useScopedTranslation('Page.WarrantyRequest');

  const format = dateFormat();

  return (
    <div>
      <div
        className={clsx(
          styles.rowContainer,
          styles.columnGap20,
          styles.marginBottom36,
        )}
      >
        {device?.deviceName && defineDeviceImage(device.deviceName)}
        <div className={clsx(styles.rowContainer, styles.columnGap16)}>
          <div className={clsx(styles.columnContainer, styles.rowGap13)}>
            <div className={clsx(styles.label)}>{i18n.t('serial_number')}</div>
            {device?.deviceName && (
              <div className={clsx(styles.label)}>{i18n.t('device_type')}</div>
            )}
          </div>
          <div className={clsx(styles.columnContainer, styles.rowGap13)}>
            <div data-test-id={'serial-number'} className={styles.text}>
              {serialNumber}
            </div>
            {device?.deviceName && (
              <div data-test-id={'device-type'} className={clsx(styles.text)}>
                {device.deviceName}
              </div>
            )}
          </div>
        </div>
      </div>
      {deviceTypes?.length ? (
        <div className={styles.relativeContainer}>
          <div
            className={clsx(
              styles.rowContainer,
              styles.justifyContentSpaceBetween,
              styles.marginBottom4,
            )}
          >
            <div className={clsx(styles.label)}>{i18n.t('device_type')}</div>
          </div>
          <Autocomplete
            data-test-id={'device-select'}
            className={clsx(
              styles.errorSelectorBorder,
              form.formState.errors.isAnyDeviceSelected &&
                styles.errorSelectorRedBorder,
            )}
            size="small"
            options={deviceTypes}
            isOptionEqualToValue={(a, b) => a.key === b.key}
            onChange={(e, newValue) => {
              form.clearErrors('isAnyDeviceSelected');
              if (newValue) {
                form.setValue('selectedDevice', newValue);
              }
            }}
            onInputChange={(e, value) => setInputValue(value)}
            getOptionLabel={(option) => option.text}
            renderInput={(params) => (
              <TextField
                {...params}
                className={clsx(styles.placeholder)}
                placeholder={i18n.t('input_device_placeholder')}
              />
            )}
            disableCloseOnSelect={true}
            popupIcon={<ArrowDown />}
            disablePortal={true}
            renderOption={(props, option) => (
              <SelectOption
                testId={'device-item'}
                key={option.key}
                option={option}
                liProps={props}
                inputValue={inputValue}
              />
            )}
          />
          {form.formState.errors.isAnyDeviceSelected && (
            <div
              className={clsx(
                styles.text,
                styles.errorText,
                styles.absoluteContainer,
              )}
            >
              {i18n.t('required_error')}
            </div>
          )}
        </div>
      ) : (
        <div>
          <div
            className={clsx(
              styles.rowContainer,
              styles.columnGap15,
              styles.marginBottom52,
            )}
          >
            <Input
              testId={'operating-hours-input'}
              {...form.register('operatingHours')}
              errorText={form.formState.errors.operatingHours?.message}
              topHelperText={i18n.t('operating_hours')}
              fullWidth={true}
              containerClassName={clsx(styles.flex1, styles.marginTop23)}
              type={'number'}
            />
            <div
              className={clsx(
                styles.columnContainer,
                styles.flex1,
                styles.alignSelfFlexStart,
                styles.rowGap3,
              )}
            >
              <div className={styles.label}>{i18n.t('resale_date')}</div>
              <DesktopDatePicker
                value={dateOfResale ?? null}
                format={format}
                onChange={(value) => {
                  if (value && value > new Date()) {
                    form.setValue('dateOfResale', new Date() ?? null, {
                      shouldDirty: true,
                    });
                  } else {
                    form.setValue('dateOfResale', value ?? null, {
                      shouldDirty: true,
                    });
                  }
                }}
                slots={{
                  openPickerIcon: CalendarIcon,
                }}
                className={styles.datePicker}
                disableFuture={true}
              />
            </div>
            {device?.manufactureYear && (
              <div
                className={clsx(
                  styles.columnContainer,
                  styles.flex1,
                  styles.rowGap13,
                  styles.alignSelfFlexStart,
                )}
              >
                <div className={styles.label}>
                  {i18n.t('year_of_manufacture')}
                </div>
                <div className={styles.text}>{device.manufactureYear}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
